import React from 'react';

import { TextLink } from '@upstart/mint-ui';
import { Box, Divider, Heading } from '@upstart/patina-design-system';

import { useIsPartnerTenant, useTenantConfig } from '@contexts/TenantConfig';
import { ConsentTag } from '@hooks/api/useStepState/types/fundingForm';
import { useSession } from '@hooks/providers/Session';
import { useBrandName } from '@hooks/useBrandName';
import { Container } from '@molecules/Container';
import { Loading } from '@organisms/Loading';
import { getLegalDocumentLink } from '@utils/legal';

export const MortgageLegal: React.FC<{}> = () => {
  const { isLoading, status } = useSession();

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Box atoms={{ paddingBottom: 'l' }}>
      <Container>
        {status === 'authenticated' ? (
          <AuthenticatedContent />
        ) : (
          <AnonymousContent />
        )}
      </Container>
    </Box>
  );
};

const AnonymousContent: React.FC = () => {
  const brandName = useBrandName();

  return (
    <div>
      <Heading component="h1" size="l" className="mb-4">
        {brandName} Legal
      </Heading>
      <Divider />
      <ul className="px-4">
        <li className="mb-2">
          <TextLink
            size="bodyMdBold"
            href={getLegalDocumentLink('website_terms_of_use')}
          >
            Terms of use
          </TextLink>
        </li>
        <li>
          <StateLicenses />
        </li>
      </ul>
    </div>
  );
};

const AuthenticatedContent: React.FC = () => {
  const brandName = useBrandName();
  const isPartner = useIsPartnerTenant();
  const { brandName: tenantBrandName } = useTenantConfig();
  const registerUsers = getAllRegisterUsers(
    isPartner ? tenantBrandName : 'Upstart',
  );

  return (
    <div>
      <Heading component="h1" size="l" className="mb-4 text-center">
        {brandName}
      </Heading>
      <Heading component="h1" size="l" className="mb-4 text-center">
        Legal Agreements, Policies and Disclosures
      </Heading>

      <div className="mt-10">
        <Heading component="h1" size="m" className="mb-4">
          For all registered users
        </Heading>
        <Divider className="mb-2" />
        <ul className="px-4">
          {Object.keys(registerUsers).map((a) => (
            <li key={a} className="mb-2">
              <TextLink size="bodyMdBold" href={`/legal/${a}`} target="_blank">
                {registerUsers[a as ConsentTag]}
              </TextLink>
            </li>
          ))}
          <li>
            <StateLicenses />
          </li>
        </ul>
      </div>
      <div className="mt-10">
        <Heading component="h1" size="m" className="mb-4">
          For applicants and borrowers
        </Heading>
        <Divider className="mb-2" />
        <ul className="px-4">
          {Object.keys(applicantAndBorrowers).map((a) => (
            <li key={a} className="mb-2">
              <TextLink size="bodyMdBold" href={`/legal/${a}`} target="_blank">
                {applicantAndBorrowers[a as ConsentTag]}
              </TextLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const StateLicenses: React.FC = () => {
  const { stateLicensesUrl } = useTenantConfig();

  return (
    <TextLink size="bodyMdBold" href={stateLicensesUrl} target="_blank">
      State licenses
    </TextLink>
  );
};

type ConsentLinkTitle = Record<ConsentTag, string>;

const getAllRegisterUsers = (brandName: string): Partial<ConsentLinkTitle> => ({
  privacy_policy: 'Privacy Policy',
  sms_consent: 'SMS consent',
  esign_consent: 'e-Sign Consent',
  platform_agreement: `${brandName} terms and conditions`,
});

const applicantAndBorrowers: Partial<ConsentLinkTitle> = {
  account_verification_consent: 'Account access service terms',
  credit_consent: 'Credit report and information verification consent',
  reg_b_appraisal_disclosure: 'Reg B appraisal disclosure',
  heloc_disclosure: 'HELOC disclosure',
  cfpb_heloc_booklet: 'CFPB HELOC booklet',
};
